export const parseCssPx = (value: string): number => {
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) ? parsed : 0;
};

export const measureChildWidths = (
  container: HTMLElement,
  childCount: number,
): number[] => {
  return Array.from(container.children)
    .slice(0, childCount)
    .map(child => child.getBoundingClientRect().width);
};

/** Simulate flex-wrap row assignment from item widths. */
export const packIntoRows = (
  widths: number[],
  trackWidth: number,
  gap: number,
): number[] => {
  if (trackWidth <= 0 || widths.length === 0) {
    return widths.map(() => 0);
  }

  const rowIndexes: number[] = [];
  let currentRow = 0;
  let rowUsedWidth = 0;

  widths.forEach(width => {
    const gapBefore = rowUsedWidth > 0 ? gap : 0;
    const nextWidth = rowUsedWidth + gapBefore + width;

    if (rowUsedWidth > 0 && nextWidth > trackWidth) {
      currentRow += 1;
      rowUsedWidth = width;
    } else {
      rowUsedWidth = nextWidth;
    }

    rowIndexes.push(currentRow);
  });

  return rowIndexes;
};

export const getControlRowIndex = (
  pillWidths: number[],
  visibleCount: number,
  controlWidth: number,
  trackWidth: number,
  gap: number,
): number | null => {
  if (visibleCount < 0) {
    return null;
  }

  const widths = [...pillWidths.slice(0, visibleCount), controlWidth];
  const rowIndexes = packIntoRows(widths, trackWidth, gap);

  return rowIndexes[rowIndexes.length - 1] ?? null;
};

export interface FindMaxVisibleIndexesParams {
  gap: number;
  getControlWidth: (hiddenCount: number) => number;
  itemCount: number;
  maxRows: number;
  pillWidths: number[];
  reserveControlSlot: boolean;
  trackWidth: number;
}

export const findMaxVisibleIndexes = ({
  gap,
  getControlWidth,
  itemCount,
  maxRows,
  pillWidths,
  reserveControlSlot,
  trackWidth,
}: FindMaxVisibleIndexesParams): number[] => {
  if (itemCount === 0 || trackWidth <= 0) {
    return [];
  }

  const widths = pillWidths.slice(0, itemCount);

  if (!reserveControlSlot) {
    const rowIndexes = packIntoRows(widths, trackWidth, gap);
    const lastRow = rowIndexes[rowIndexes.length - 1] ?? 0;

    if (lastRow < maxRows) {
      return widths.map((_, index) => index);
    }

    const allowedRows = new Set(
      rowIndexes
        .map((rowIndex, index) => (rowIndex < maxRows ? index : null))
        .filter((index): index is number => index !== null),
    );

    return [...allowedRows];
  }

  const allFitRowIndexes = packIntoRows(widths, trackWidth, gap);
  const lastRow = allFitRowIndexes[allFitRowIndexes.length - 1];

  if (lastRow !== undefined && lastRow < maxRows) {
    return widths.map((_, index) => index);
  }

  for (let visibleCount = itemCount; visibleCount >= 1; visibleCount -= 1) {
    const hiddenCount = itemCount - visibleCount;
    const controlWidth = getControlWidth(Math.max(hiddenCount, 1));
    const controlRow = getControlRowIndex(
      widths,
      visibleCount,
      controlWidth,
      trackWidth,
      gap,
    );

    if (controlRow !== null && controlRow < maxRows) {
      return Array.from({ length: visibleCount }, (_, index) => index);
    }
  }

  return [0];
};
